<script setup>
import { computed, reactive, ref, useTemplateRef, watch } from 'vue'
import * as v from 'valibot'
import { useStoryblokStore } from '@/stores/storyblok'
import { useTOOStore } from '@/stores/too'

const emit = defineEmits(['completed'])

const ClientSchema = computed(() => {
  return v.objectAsync({
    space: v.pipeAsync(
      v.looseObject({ label: v.string() }, 'You must select a space'),
      v.checkAsync(async (input) => {
        const siteExists = await storyblokStore.checkIfSiteExists(input.id, storyblokStore.siteSlug)
        return !siteExists
      }, `This space already has a site with slug "${storyblokStore.siteSlug}"`),
    ),
    company: v.looseObject({ label: v.string() }, 'You must select a company'),
  })
})

const storyblokStore = useStoryblokStore()
const tooStore = useTOOStore()

const state = reactive({
  space: storyblokStore.space || '',
  company: storyblokStore.company || '',
})

const spaces = computed(() => {
  return storyblokStore.spaces.map((space) => ({
    ...space,
    label: space.name,
  }))
})

const companies = computed(() => {
  return tooStore.companies.map((company) => ({
    ...company,
    label: company.name,
    description: null,
  }))
})

const missingCompanyFields = ref([])
const showCompletenessCheck = ref(false)

async function repeatCompanyCheck() {
  missingCompanyFields.value = await tooStore.checkCompanyCompleteness(state.company)
  if (!missingCompanyFields.value.length) {
    storyblokStore.space = state.space
    storyblokStore.company = state.company
    emit('completed')
  }
}

watch(
  () => state.company,
  () => {
    showCompletenessCheck.value = false
  },
)

const form = useTemplateRef('form')
defineExpose({
  async validate() {
    const validData = await form.value.validate()
    if (validData) {
      // We already showed the warning panel about missing fields, go to the next step
      if (showCompletenessCheck.value) {
        storyblokStore.space = state.space
        storyblokStore.company = state.company
        return true
      }

      missingCompanyFields.value = await tooStore.checkCompanyCompleteness(state.company)
      if (missingCompanyFields.value.length) {
        showCompletenessCheck.value = true
        return false
      } else {
        storyblokStore.space = state.space
        storyblokStore.company = state.company
        return true
      }
    }
    return false
  },
})
</script>

<template>
  <h2 class="text-primary text-3xl mb-8">Client configuration</h2>
  <UForm
    ref="form"
    :schema="ClientSchema"
    :validate-on="['change', 'submit']"
    :state="state"
    class="w-full space-y-4"
  >
    <UFormField
      label="Space"
      name="space"
      help="If you don't see the space you need here, contact the development team"
      required
    >
      <USelectMenu
        placeholder="Select space"
        v-model="state.space"
        :items="spaces"
        :loading="!spaces.length"
        class="w-full"
        :ui="{ viewport: 'light-scrollbar' }"
      />
    </UFormField>
    <UFormField
      label="Company"
      name="company"
      help="This is the client's company in the Publisher"
      required
    >
      <USelectMenu
        placeholder="Select company"
        v-model="state.company"
        :items="companies"
        virtualize
        :loading="!companies.length"
        class="w-full"
        :ui="{ viewport: 'light-scrollbar' }"
      />
    </UFormField>
  </UForm>

  <CompanyCompletenessResult
    v-if="showCompletenessCheck"
    :company="state.company"
    :missing-fields="missingCompanyFields"
    @repeat-check="repeatCompanyCheck()"
  ></CompanyCompletenessResult>
</template>
